import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Origin } from "@/hooks/useGuests";
import { Property } from "@/types/property";
import { Organization } from "@/hooks/useOrganizations";
import { PropertyContract } from "@/hooks/useContracts";
import OrganizationsMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect";
import PropertiesMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect";
import DateRangePresetPicker from "@/components/v2/molecules/admin/shared/DateRangePresetPicker";
import { Search, X } from "lucide-react";
import React, { useMemo } from "react";

interface BookingsFiltersProps {
  guestSearch: string;
  selectedProperties: string[];
  selectedOrganizations: string[];
  selectedOrigin: string;
  checkInDate?: Date;
  checkOutDate?: Date;
  setGuestSearch: (value: string) => void;
  setSelectedProperties: (value: string[]) => void;
  setSelectedOrganizations: (value: string[]) => void;
  setSelectedOrigin: (value: string) => void;
  setCheckInDate: (date: Date | undefined) => void;
  setCheckOutDate: (date: Date | undefined) => void;
  properties: Property[];
  organizations: Organization[];
  contracts: PropertyContract[];
  origins: Origin[];
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
}

const BookingsFilters: React.FC<BookingsFiltersProps> = ({
  guestSearch,
  selectedProperties,
  selectedOrganizations,
  selectedOrigin,
  checkInDate,
  checkOutDate,
  setGuestSearch,
  setSelectedProperties,
  setSelectedOrganizations,
  setSelectedOrigin,
  setCheckInDate,
  setCheckOutDate,
  properties,
  organizations,
  contracts,
  origins,
  hasActiveFilters,
  clearAllFilters,
}) => {
  // Filter properties based on selected organizations
  const filteredProperties = useMemo(() => {
    if (selectedOrganizations.length === 0) {
      return properties;
    }

    const orgPropertyIds = contracts
      .filter(c => 
        c.status === 'active' && 
        c.organization_id &&
        selectedOrganizations.includes(c.organization_id)
      )
      .map(c => c.property_id);

    return properties.filter(p => orgPropertyIds.includes(p.id));
  }, [properties, selectedOrganizations, contracts]);

  const handleDateRangeChange = (from: Date | undefined, to: Date | undefined) => {
    setCheckInDate(from);
    setCheckOutDate(to);
  };

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar huéspedes"
            value={guestSearch}
            onChange={(e) => setGuestSearch(e.target.value)}
            className="pl-9 w-[200px] rounded-full bg-muted/50 hover:bg-muted transition-colors border-0 h-10"
          />
        </div>

        <OrganizationsMultiSelect
          organizations={organizations}
          selectedOrganizations={selectedOrganizations}
          onSelectionChange={setSelectedOrganizations}
        />

        <PropertiesMultiSelect
          properties={filteredProperties}
          selectedProperties={selectedProperties}
          onSelectionChange={setSelectedProperties}
        />

        <Select value={selectedOrigin} onValueChange={setSelectedOrigin}>
          <SelectTrigger className="w-[180px] rounded-full bg-muted/50 hover:bg-muted transition-colors border-0">
            <SelectValue placeholder="Todos los orígenes" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">Todos los orígenes</SelectItem>
            {origins.map((origin) => (
              <SelectItem key={origin.id} value={origin.id}>
                {origin.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <DateRangePresetPicker
          dateFrom={checkInDate}
          dateTo={checkOutDate}
          onDateRangeChange={handleDateRangeChange}
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearAllFilters}
            className="h-10 w-10 rounded-full bg-muted/50 hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default BookingsFilters;
